import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const GeographyDemographicsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/financial-analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!analytics) return '0.00';
    const curr = analytics.currency || 'GBP';
    const symbol = curr === 'GBP' ? '£' : curr === 'USD' ? '$' : '€';
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading geographic & demographic analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Geography & Demographics</h1>
          <p className="text-purple-200 text-sm">Customer location and revenue distribution analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Geography & Demographics</h1>
              <p className="text-purple-200 text-sm">Customer location and revenue distribution analytics</p>
            </div>
          </div>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-gray-600">
        Last updated: {formatDateTime(analytics.last_updated)}
      </div>

      <div className="space-y-8">
        {/* Chart 15: Revenue by Organisation (Top 10) */}
        {analytics && analytics.revenue_by_organisation && analytics.revenue_by_organisation.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Revenue-Generating Organisations</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={analytics.revenue_by_organisation}
                layout="vertical"
                margin={{ left: 120 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="organisation" type="category" width={110} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="revenue" fill="#6366f1" name="Monthly Revenue" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> Your top 10 highest revenue-generating customers ranked by their monthly subscription value.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> These are your key accounts - protect them at all costs. If 80% of revenue comes from 20% of customers (Pareto principle), focus on retention and satisfaction for these top accounts. Consider dedicated account managers, priority support, or exclusive features for high-value customers to reduce churn risk.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Revenue-Generating Organisations</h3>
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              No revenue data available. Create PayPal subscriptions to see data.
            </div>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> Your top 10 highest revenue-generating customers ranked by their monthly subscription value.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> These are your key accounts - protect them at all costs. If 80% of revenue comes from 20% of customers (Pareto principle), focus on retention and satisfaction for these top accounts. Consider dedicated account managers, priority support, or exclusive features for high-value customers to reduce churn risk.
              </p>
            </div>
          </div>
        )}

        {/* Chart 16: Subscriptions by Geographic Location */}
        {analytics && analytics.subscriptions_by_location && Object.keys(analytics.subscriptions_by_location).length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscriptions by Geographic Location</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart 
                data={Object.entries(analytics.subscriptions_by_location)
                  .map(([city, count]) => ({ city, count }))
                  .sort((a, b) => b.count - a.count)
                }
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="city" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#0ea5e9" name="Active Subscriptions" />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The number of active subscriptions distributed by city/location where your customers are based.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Identifies your strongest geographic markets. High concentration in specific cities? Consider localized marketing, regional sales teams, or city-specific features. Low penetration in key markets? Opportunity for targeted expansion. Use this to plan marketing budgets, event locations, and regional partnerships.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscriptions by Geographic Location</h3>
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No location data available. Organisations need city/address information.
            </div>
            <div className="mt-4 p-4 bg-sky-50 border border-sky-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The number of active subscriptions distributed by city/location where your customers are based.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Identifies your strongest geographic markets. High concentration in specific cities? Consider localized marketing, regional sales teams, or city-specific features. Low penetration in key markets? Opportunity for targeted expansion. Use this to plan marketing budgets, event locations, and regional partnerships.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeographyDemographicsPage;
